import React, { useMemo } from 'react';
import {
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Visibility as VisibilityIcon } from '@mui/icons-material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { VentasResumenProductoRow } from '../../services/ventasInformesService';
import { labelTipoArticulo } from '../../config/tipoArticuloOptions';
import {
  aggregateByField,
  FilaAgregada,
  isSameProductoResumenRow,
  normalizarClasificacion,
  pctDelMonto,
  pctRentabilidad,
  sumRows,
} from '../../utils/agruparVentasResumenProducto';
import {
  buildBarrasPorCampoMes,
  buildSerieProductoMensual,
  buildSerieRamoAgrupada,
  CHART_MES_KEY,
  chartColorAt,
  groupRowsByMes,
} from '../../utils/agruparVentasResumenProductoMensual';
import { formatCurrency } from '../../utils/formatters';

const TAB_RAMO = 0;
const TAB_LINEA = 1;
const TAB_CLASE = 2;
const TAB_PRODUCTO = 3;

const CHIP_CLASIFICACION_SX = {
  maxWidth: '100%',
  height: 'auto',
  minHeight: 22,
  alignSelf: 'flex-start',
  '& .MuiChip-label': {
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
    overflow: 'visible',
    fontSize: '0.6875rem',
    lineHeight: 1.35,
    px: '6px',
    py: '4px',
    fontWeight: 500,
    display: 'block',
  },
} as const;

const RESUMEN_COL = {
  producto: { width: '14%' },
  tipoArt: { width: '6%' },
  ramo: { width: '7%' },
  linea: { width: '7%' },
  clase: { width: '7%' },
  cantidad: { width: '5%' },
  monto: { width: '9%' },
  pctMonto: { width: '6%' },
  costo: { width: '9%' },
  pctRentab: { width: '6%' },
  peso: { width: '7%' },
  volumen: { width: '7%' },
  accion: { width: '5%' },
} as const;

const currencyFormatter = new Intl.NumberFormat('es-AR', {
  style: 'currency',
  currency: 'ARS',
  minimumFractionDigits: 2,
});

const qtyFormatter = new Intl.NumberFormat('es-AR', {
  maximumFractionDigits: 0,
});

const decimal3Formatter = new Intl.NumberFormat('es-AR', {
  minimumFractionDigits: 3,
  maximumFractionDigits: 3,
});

const pctFormatter = new Intl.NumberFormat('es-AR', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

const fmtPctRentab = (monto: number, costo: number): string => {
  const pct = pctRentabilidad(monto, costo);
  return pct != null ? `${pctFormatter.format(pct)}%` : '—';
};

type ResumenClasificacionTabsProps = {
  rows: VentasResumenProductoRow[];
  rowsMensuales: VentasResumenProductoRow[];
  totalMonto: number;
  totales: {
    totalCantidad: number;
    totalMonto: number;
    totalCosto: number;
    totalPeso: number;
    totalVolumen: number;
  };
  onAbrirDetalle: (row: VentasResumenProductoRow) => void;
};

type NivelChip = 'ramo' | 'linea' | 'clase';

type FiltroDrillDown = {
  nivel: NivelChip;
  nombre: string;
};

const CAMPO_POR_NIVEL: Record<NivelChip, 'nombreRamo' | 'nombreLinea' | 'nombreClase'> = {
  ramo: 'nombreRamo',
  linea: 'nombreLinea',
  clase: 'nombreClase',
};

const LABEL_NIVEL: Record<NivelChip, string> = {
  ramo: 'Ramo',
  linea: 'Línea',
  clase: 'Clase',
};

function filtrarRowsPorDrillDown(rows: VentasResumenProductoRow[], filtro: FiltroDrillDown): VentasResumenProductoRow[] {
  const campo = CAMPO_POR_NIVEL[filtro.nivel];
  return rows.filter((r) => normalizarClasificacion(r[campo]) === filtro.nombre);
}

const CHIP_NIVEL_STYLES: Record<NivelChip, { bgcolor: string; color: string }> = {
  ramo: { bgcolor: '#e3f2fd', color: '#1565c0' },
  linea: { bgcolor: '#e8f5e9', color: '#2e7d32' },
  clase: { bgcolor: '#f3e5f5', color: '#6a1b9a' },
};

const ChipClasificacion: React.FC<{ nivel: NivelChip; nombre: string }> = ({ nivel, nombre }) => {
  const t = (nombre ?? '').trim();
  if (!t) return <>—</>;
  const { bgcolor, color } = CHIP_NIVEL_STYLES[nivel];
  return (
    <Chip
      label={t}
      size="small"
      sx={{
        ...CHIP_CLASIFICACION_SX,
        bgcolor,
        color,
      }}
    />
  );
};

const renderCardCampo = (label: string, valor: React.ReactNode) => (
  <Box>
    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ wordBreak: 'break-word', fontVariantNumeric: 'tabular-nums' }}>
      {valor}
    </Typography>
  </Box>
);

type TablaAgregadaProps = {
  label: string;
  nivel: NivelChip;
  filas: FilaAgregada[];
  totales: ResumenClasificacionTabsProps['totales'];
  onDrillDown: (nivel: NivelChip, nombre: string) => void;
};

const TablaAgregada: React.FC<TablaAgregadaProps> = ({ label, nivel, filas, totales, onDrillDown }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  if (isMobile) {
    return (
      <Stack spacing={1.5}>
        {filas.map((f) => (
          <Paper
            key={f.nombre}
            variant="outlined"
            onClick={() => onDrillDown(nivel, f.nombre)}
            sx={{
              p: 2,
              borderRadius: 2,
              cursor: 'pointer',
              '&:hover': { boxShadow: '0 4px 8px rgba(0,0,0,0.08)' },
            }}
          >
            <Stack spacing={1.5}>
              <ChipClasificacion nivel={nivel} nombre={f.nombre} />
              <Grid container spacing={1.5}>
                <Grid item xs={6}>
                  {renderCardCampo('Cant.', qtyFormatter.format(f.cantidad))}
                </Grid>
                <Grid item xs={6}>
                  {renderCardCampo('Monto', currencyFormatter.format(f.montoTotal))}
                </Grid>
                <Grid item xs={6}>
                  {renderCardCampo('% Monto', `${pctFormatter.format(f.pctMonto)}%`)}
                </Grid>
                <Grid item xs={6}>
                  {renderCardCampo('% Rentab.', fmtPctRentab(f.montoTotal, f.costoTotal))}
                </Grid>
                <Grid item xs={6}>
                  {renderCardCampo('Costo', currencyFormatter.format(f.costoTotal))}
                </Grid>
                <Grid item xs={6}>
                  {renderCardCampo('Peso (kg)', decimal3Formatter.format(f.pesoTotal))}
                </Grid>
                <Grid item xs={6}>
                  {renderCardCampo('Vol. (m³)', decimal3Formatter.format(f.volumenTotal))}
                </Grid>
              </Grid>
              <Typography variant="caption" color="primary" sx={{ fontWeight: 600 }}>
                Tocá para ver productos de este {label.toLowerCase()}
              </Typography>
            </Stack>
          </Paper>
        ))}
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#e3f2fd' }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
            TOTAL
          </Typography>
          <Grid container spacing={1.5}>
            <Grid item xs={6}>
              {renderCardCampo('Cant.', qtyFormatter.format(totales.totalCantidad))}
            </Grid>
            <Grid item xs={6}>
              {renderCardCampo('Monto', currencyFormatter.format(totales.totalMonto))}
            </Grid>
            <Grid item xs={6}>
              {renderCardCampo('% Monto', '100%')}
            </Grid>
            <Grid item xs={6}>
              {renderCardCampo('% Rentab.', fmtPctRentab(totales.totalMonto, totales.totalCosto))}
            </Grid>
            <Grid item xs={6}>
              {renderCardCampo('Costo', currencyFormatter.format(totales.totalCosto))}
            </Grid>
            <Grid item xs={6}>
              {renderCardCampo('Peso (kg)', decimal3Formatter.format(totales.totalPeso))}
            </Grid>
            <Grid item xs={6}>
              {renderCardCampo('Vol. (m³)', decimal3Formatter.format(totales.totalVolumen))}
            </Grid>
          </Grid>
        </Paper>
      </Stack>
    );
  }

  return (
  <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
    <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 640 }}>
      <TableHead>
        <TableRow sx={{ bgcolor: '#fafafa' }}>
          <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>{label}</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.72rem' }}>
            Cant.
          </TableCell>
          <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.72rem' }}>
            Monto
          </TableCell>
          <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.72rem' }}>
            % Monto
          </TableCell>
          <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.72rem' }}>
            Costo
          </TableCell>
          <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.72rem' }}>
            % Rentab.
          </TableCell>
          <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.72rem' }}>
            Peso (kg)
          </TableCell>
          <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.72rem' }}>
            Vol. (m³)
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {filas.map((f) => (
          <TableRow
            key={f.nombre}
            hover
            onClick={() => onDrillDown(nivel, f.nombre)}
            sx={{ cursor: 'pointer' }}
          >
            <TableCell sx={{ py: 1, verticalAlign: 'top', overflow: 'visible' }}>
              <Tooltip title={`Ver productos de este ${label.toLowerCase()}`} arrow>
                <Box component="span" sx={{ display: 'inline-block' }}>
                  <ChipClasificacion nivel={nivel} nombre={f.nombre} />
                </Box>
              </Tooltip>
            </TableCell>
            <TableCell align="right" sx={{ fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums' }}>
              {qtyFormatter.format(f.cantidad)}
            </TableCell>
            <TableCell align="right" sx={{ fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums' }}>
              {currencyFormatter.format(f.montoTotal)}
            </TableCell>
            <TableCell align="right" sx={{ fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums' }}>
              {pctFormatter.format(f.pctMonto)}%
            </TableCell>
            <TableCell align="right" sx={{ fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums' }}>
              {currencyFormatter.format(f.costoTotal)}
            </TableCell>
            <TableCell align="right" sx={{ fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums' }}>
              {fmtPctRentab(f.montoTotal, f.costoTotal)}
            </TableCell>
            <TableCell align="right" sx={{ fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums' }}>
              {decimal3Formatter.format(f.pesoTotal)}
            </TableCell>
            <TableCell align="right" sx={{ fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums' }}>
              {decimal3Formatter.format(f.volumenTotal)}
            </TableCell>
          </TableRow>
        ))}
        <TableRow sx={{ bgcolor: '#e3f2fd', '& td': { fontWeight: 700 } }}>
          <TableCell sx={{ fontSize: '0.8rem' }}>TOTAL</TableCell>
          <TableCell align="right" sx={{ fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums' }}>
            {qtyFormatter.format(totales.totalCantidad)}
          </TableCell>
          <TableCell align="right" sx={{ fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums' }}>
            {currencyFormatter.format(totales.totalMonto)}
          </TableCell>
          <TableCell align="right" sx={{ fontSize: '0.75rem' }}>
            100%
          </TableCell>
          <TableCell align="right" sx={{ fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums' }}>
            {currencyFormatter.format(totales.totalCosto)}
          </TableCell>
          <TableCell align="right" sx={{ fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums' }}>
            {fmtPctRentab(totales.totalMonto, totales.totalCosto)}
          </TableCell>
          <TableCell align="right" sx={{ fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums' }}>
            {decimal3Formatter.format(totales.totalPeso)}
          </TableCell>
          <TableCell align="right" sx={{ fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums' }}>
            {decimal3Formatter.format(totales.totalVolumen)}
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  </TableContainer>
  );
};

const chartTooltipStyle = {
  borderRadius: 0,
  border: '1px solid #e0e0e0',
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
} as const;

type TooltipPayloadItem = {
  name?: string | number;
  value?: number | string;
  color?: string;
  dataKey?: string | number;
};

const TooltipRamoCompacto: React.FC<{
  active?: boolean;
  label?: string | number;
  payload?: TooltipPayloadItem[];
}> = ({ active, label, payload }) => {
  if (!active || !payload?.length) return null;

  const items = [...payload]
    .map((p) => ({
      name: String(p.name ?? p.dataKey ?? ''),
      value: Number(p.value) || 0,
      color: p.color ?? '#1976d2',
    }))
    .filter((p) => Math.abs(p.value) > 1e-9)
    .sort((a, b) => Math.abs(b.value) - Math.abs(a.value));

  if (items.length === 0) return null;

  const total = items.reduce((acc, i) => acc + i.value, 0);

  return (
    <Box
      sx={{
        ...chartTooltipStyle,
        bgcolor: '#fff',
        px: 1,
        py: 0.75,
        minWidth: 140,
        maxWidth: 200,
        maxHeight: 160,
        overflowY: 'auto',
      }}
    >
      <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ mb: 0.5 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#333' }}>{label}</Typography>
        <Typography sx={{ fontSize: 11, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
          {formatCurrency(total)}
        </Typography>
      </Stack>
      <Stack spacing={0.25}>
        {items.map((item) => (
          <Stack
            key={item.name}
            direction="row"
            spacing={0.75}
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  bgcolor: item.color,
                  flexShrink: 0,
                }}
              />
              <Typography
                noWrap
                title={item.name}
                sx={{ fontSize: 10, color: '#555', lineHeight: 1.2 }}
              >
                {item.name}
              </Typography>
            </Stack>
            <Typography
              sx={{
                fontSize: 10,
                fontWeight: 600,
                fontVariantNumeric: 'tabular-nums',
                flexShrink: 0,
                pl: 0.5,
              }}
            >
              {formatCurrency(item.value)}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};

const GraficoRamosMensual: React.FC<{ rowsMensuales: VentasResumenProductoRow[] }> = ({ rowsMensuales }) => {
  const { data, series } = useMemo(() => buildSerieRamoAgrupada(rowsMensuales, 8), [rowsMensuales]);
  if (data.length === 0 || series.length === 0) return null;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        mb: 2,
        borderRadius: 3,
        borderColor: '#e0e0e0',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
      }}
    >
      <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: '#1976d2' }}>
        Ventas por ramo (mensual)
      </Typography>
      <Box sx={{ width: '100%', height: 320 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 8, right: 12, left: 8, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
            <XAxis dataKey={CHART_MES_KEY} tick={{ fill: '#666', fontSize: 12 }} stroke="#999" />
            <YAxis tick={{ fill: '#666', fontSize: 12 }} stroke="#999" />
            <RechartsTooltip
              content={<TooltipRamoCompacto />}
              cursor={{ fill: 'rgba(25, 118, 210, 0.06)' }}
            />
            <Legend wrapperStyle={{ paddingTop: 8 }} />
            {series.map((s, i) => (
              <Bar key={s} dataKey={s} name={s} fill={chartColorAt(i)} radius={[6, 6, 0, 0]} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Paper>
  );
};

const GraficosMensualesPorCampo: React.FC<{
  rowsMensuales: VentasResumenProductoRow[];
  field: 'nombreLinea' | 'nombreClase';
  titulo: string;
}> = ({ rowsMensuales, field, titulo }) => {
  const buckets = useMemo(() => groupRowsByMes(rowsMensuales), [rowsMensuales]);
  if (buckets.length === 0) return null;

  return (
    <Stack spacing={2} sx={{ mb: 2 }}>
      {buckets.map((b) => {
        const series = buildBarrasPorCampoMes(b.rows, field, 10);
        if (series.length === 0) return null;
        return (
          <Paper
            key={b.key}
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 3,
              borderColor: '#e0e0e0',
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: '#1976d2' }}>
              {titulo} — {b.label}
            </Typography>
            <Box sx={{ width: '100%', height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={series} margin={{ top: 8, right: 12, left: 8, bottom: 48 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis
                    dataKey="nombre"
                    tick={{ fill: '#666', fontSize: 11 }}
                    stroke="#999"
                    interval={0}
                    angle={-25}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis tick={{ fill: '#666', fontSize: 12 }} stroke="#999" />
                  <RechartsTooltip
                    formatter={(value: number) => formatCurrency(value)}
                    contentStyle={chartTooltipStyle}
                  />
                  <Bar dataKey="monto" name="Ventas" radius={[6, 6, 0, 0]}>
                    {series.map((_, i) => (
                      <Cell key={`${b.key}-${i}`} fill={chartColorAt(i)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        );
      })}
    </Stack>
  );
};

const GraficoProductoMensual: React.FC<{
  rowsMensuales: VentasResumenProductoRow[];
  producto: VentasResumenProductoRow | null;
}> = ({ rowsMensuales, producto }) => {
  const series = useMemo(
    () => (producto ? buildSerieProductoMensual(rowsMensuales, producto) : []),
    [rowsMensuales, producto]
  );

  if (!producto) {
    return (
      <Typography color="text.secondary" variant="body2" sx={{ mb: 2 }}>
        Seleccioná un producto para ver el gráfico mensual.
      </Typography>
    );
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        mb: 2,
        borderRadius: 3,
        borderColor: '#e0e0e0',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
      }}
    >
      <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600, color: '#1976d2' }}>
        Ventas mensuales
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {producto.producto || '(sin nombre)'}
      </Typography>
      <Box sx={{ width: '100%', height: 280 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={series} margin={{ top: 8, right: 12, left: 8, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
            <XAxis dataKey="nombre" tick={{ fill: '#666', fontSize: 12 }} stroke="#999" />
            <YAxis tick={{ fill: '#666', fontSize: 12 }} stroke="#999" />
            <RechartsTooltip
              formatter={(value: number) => formatCurrency(value)}
              contentStyle={chartTooltipStyle}
            />
            <Bar dataKey="monto" name="Ventas" fill="#1976d2" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Paper>
  );
};

type TablaProductosProps = {
  rows: VentasResumenProductoRow[];
  totalMonto: number;
  totales: ResumenClasificacionTabsProps['totales'];
  filtroDrillDown: FiltroDrillDown | null;
  productoSeleccionado: VentasResumenProductoRow | null;
  onLimpiarFiltro: () => void;
  onSeleccionarProducto: (row: VentasResumenProductoRow) => void;
  onAbrirDetalle: (row: VentasResumenProductoRow) => void;
};

const TablaProductos: React.FC<TablaProductosProps> = ({
  rows,
  totalMonto,
  totales,
  filtroDrillDown,
  productoSeleccionado,
  onLimpiarFiltro,
  onSeleccionarProducto,
  onAbrirDetalle,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const filasOrdenadas = useMemo(
    () => [...rows].sort((a, b) => b.montoTotal - a.montoTotal),
    [rows]
  );

  return (
    <Box>
      {filtroDrillDown ? (
        <Box sx={{ mb: 1.5 }}>
          <Chip
            label={`${LABEL_NIVEL[filtroDrillDown.nivel]}: ${filtroDrillDown.nombre} · ${filasOrdenadas.length} producto${filasOrdenadas.length === 1 ? '' : 's'}`}
            size="small"
            onDelete={onLimpiarFiltro}
            sx={{
              height: 'auto',
              minHeight: 28,
              maxWidth: '100%',
              fontWeight: 600,
              ...CHIP_NIVEL_STYLES[filtroDrillDown.nivel],
              '& .MuiChip-label': {
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                overflowWrap: 'anywhere',
                py: 0.75,
                lineHeight: 1.35,
              },
            }}
          />
        </Box>
      ) : null}
      {filasOrdenadas.length === 0 ? (
        <Typography color="text.secondary" sx={{ py: 3 }}>
          No hay productos para el filtro seleccionado.
        </Typography>
      ) : isMobile ? (
        <Stack spacing={1.5}>
          {filasOrdenadas.map((row, idx) => {
            const seleccionado =
              productoSeleccionado != null && isSameProductoResumenRow(row, productoSeleccionado);
            return (
              <Paper
                key={`${row.producto}-${row.tipo}-${row.nombreRamo}-${row.nombreLinea}-${row.nombreClase}-${idx}`}
                variant="outlined"
                onClick={() => onSeleccionarProducto(row)}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  cursor: 'pointer',
                  borderColor: seleccionado ? 'primary.main' : 'divider',
                  bgcolor: seleccionado ? 'rgba(25, 118, 210, 0.08)' : undefined,
                  '&:hover': { boxShadow: '0 4px 8px rgba(0,0,0,0.08)' },
                }}
              >
                <Stack spacing={1.5}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ wordBreak: 'break-word' }}>
                    {row.producto || '—'}
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" useFlexGap gap={0.75}>
                    {row.tipo ? (
                      <Chip
                        label={labelTipoArticulo(row.tipo)}
                        size="small"
                        sx={{ bgcolor: '#fff9c4', color: '#5d4037', fontWeight: 500 }}
                      />
                    ) : null}
                    <ChipClasificacion nivel="ramo" nombre={row.nombreRamo ?? ''} />
                    <ChipClasificacion nivel="linea" nombre={row.nombreLinea ?? ''} />
                    <ChipClasificacion nivel="clase" nombre={row.nombreClase ?? ''} />
                  </Stack>
                  <Grid container spacing={1.5}>
                    <Grid item xs={6}>
                      {renderCardCampo('Cant.', qtyFormatter.format(row.cantidad))}
                    </Grid>
                    <Grid item xs={6}>
                      {renderCardCampo('Monto', currencyFormatter.format(row.montoTotal))}
                    </Grid>
                    <Grid item xs={6}>
                      {renderCardCampo(
                        '% Monto',
                        `${pctFormatter.format(pctDelMonto(row.montoTotal, totalMonto))}%`
                      )}
                    </Grid>
                    <Grid item xs={6}>
                      {renderCardCampo('% Rentab.', fmtPctRentab(row.montoTotal, row.costoTotal ?? 0))}
                    </Grid>
                    <Grid item xs={6}>
                      {renderCardCampo('Costo', currencyFormatter.format(row.costoTotal ?? 0))}
                    </Grid>
                    <Grid item xs={6}>
                      {renderCardCampo('Peso (kg)', decimal3Formatter.format(row.pesoTotal))}
                    </Grid>
                    <Grid item xs={6}>
                      {renderCardCampo('Vol. (m³)', decimal3Formatter.format(row.volumenTotal))}
                    </Grid>
                  </Grid>
                  <Button
                    size="small"
                    variant="outlined"
                    color="info"
                    fullWidth
                    startIcon={<VisibilityIcon fontSize="small" />}
                    onClick={(e) => {
                      e.stopPropagation();
                      onAbrirDetalle(row);
                    }}
                    sx={{ textTransform: 'none' }}
                  >
                    Ver detalle
                  </Button>
                </Stack>
              </Paper>
            );
          })}
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#e3f2fd' }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, color: 'primary.main' }}>
              {filtroDrillDown ? 'TOTAL FILTRADO' : 'TOTAL GENERAL'}
            </Typography>
            <Grid container spacing={1.5}>
              <Grid item xs={6}>
                {renderCardCampo('Cant.', qtyFormatter.format(totales.totalCantidad))}
              </Grid>
              <Grid item xs={6}>
                {renderCardCampo('Monto', currencyFormatter.format(totales.totalMonto))}
              </Grid>
              <Grid item xs={6}>
                {renderCardCampo('% Monto', '100%')}
              </Grid>
              <Grid item xs={6}>
                {renderCardCampo('% Rentab.', fmtPctRentab(totales.totalMonto, totales.totalCosto))}
              </Grid>
              <Grid item xs={6}>
                {renderCardCampo('Costo', currencyFormatter.format(totales.totalCosto))}
              </Grid>
              <Grid item xs={6}>
                {renderCardCampo('Peso (kg)', decimal3Formatter.format(totales.totalPeso))}
              </Grid>
              <Grid item xs={6}>
                {renderCardCampo('Vol. (m³)', decimal3Formatter.format(totales.totalVolumen))}
              </Grid>
            </Grid>
          </Paper>
        </Stack>
      ) : (
    <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
      <Table
        size="small"
        sx={{
          width: '100%',
          minWidth: 900,
          tableLayout: 'fixed',
          '& .MuiTableCell-root': { px: 0.75, py: 0.6 },
        }}
      >
        <TableHead>
          <TableRow sx={{ bgcolor: '#fafafa' }}>
            <TableCell sx={{ ...RESUMEN_COL.producto, fontSize: '0.75rem', fontWeight: 700 }}>
              Producto
            </TableCell>
            <TableCell sx={{ ...RESUMEN_COL.tipoArt, fontSize: '0.7rem', fontWeight: 700 }} title="Tipo de artículo">
              Tipo
            </TableCell>
            <TableCell sx={{ ...RESUMEN_COL.ramo, fontSize: '0.75rem', fontWeight: 700 }}>Ramo</TableCell>
            <TableCell sx={{ ...RESUMEN_COL.linea, fontSize: '0.75rem', fontWeight: 700 }}>Línea</TableCell>
            <TableCell sx={{ ...RESUMEN_COL.clase, fontSize: '0.75rem', fontWeight: 700 }}>Clase</TableCell>
            <TableCell align="right" sx={{ ...RESUMEN_COL.cantidad, fontSize: '0.72rem', fontWeight: 700 }}>
              Cant.
            </TableCell>
            <TableCell align="right" sx={{ ...RESUMEN_COL.monto, fontSize: '0.72rem', fontWeight: 700 }}>
              Monto
            </TableCell>
            <TableCell align="right" sx={{ ...RESUMEN_COL.pctMonto, fontSize: '0.72rem', fontWeight: 700 }}>
              % Monto
            </TableCell>
            <TableCell align="right" sx={{ ...RESUMEN_COL.costo, fontSize: '0.72rem', fontWeight: 700 }}>
              Costo
            </TableCell>
            <TableCell align="right" sx={{ ...RESUMEN_COL.pctRentab, fontSize: '0.72rem', fontWeight: 700 }}>
              % Rentab.
            </TableCell>
            <TableCell align="right" sx={{ ...RESUMEN_COL.peso, fontSize: '0.72rem', fontWeight: 700 }}>
              Peso (kg)
            </TableCell>
            <TableCell align="right" sx={{ ...RESUMEN_COL.volumen, fontSize: '0.72rem', fontWeight: 700 }}>
              Vol. (m³)
            </TableCell>
            <TableCell
              align="center"
              className="no-print"
              aria-hidden
              sx={{ ...RESUMEN_COL.accion, width: RESUMEN_COL.accion.width, px: 0.25 }}
            />
          </TableRow>
        </TableHead>
        <TableBody>
          {filasOrdenadas.map((row, idx) => {
            const seleccionado =
              productoSeleccionado != null && isSameProductoResumenRow(row, productoSeleccionado);
            return (
            <TableRow
              key={`${row.producto}-${row.tipo}-${row.nombreRamo}-${row.nombreLinea}-${row.nombreClase}-${idx}`}
              hover
              selected={seleccionado}
              onClick={() => onSeleccionarProducto(row)}
              sx={{
                cursor: 'pointer',
                '&:nth-of-type(odd)': { bgcolor: seleccionado ? undefined : '#fafafa' },
                '&.Mui-selected': { bgcolor: 'rgba(25, 118, 210, 0.08)' },
                '&.Mui-selected:hover': { bgcolor: 'rgba(25, 118, 210, 0.12)' },
              }}
            >
              <TableCell
                sx={{
                  ...RESUMEN_COL.producto,
                  verticalAlign: 'top',
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  overflowWrap: 'anywhere',
                  fontWeight: 700,
                  fontSize: '0.8125rem',
                  lineHeight: 1.35,
                }}
              >
                {row.producto || '—'}
              </TableCell>
              <TableCell sx={{ ...RESUMEN_COL.tipoArt, verticalAlign: 'top', overflow: 'visible' }}>
                {row.tipo ? (
                  <Chip
                    label={labelTipoArticulo(row.tipo)}
                    size="small"
                    sx={{
                      maxWidth: '100%',
                      bgcolor: '#fff9c4',
                      color: '#5d4037',
                      fontWeight: 500,
                      height: 'auto',
                      minHeight: 22,
                      alignSelf: 'flex-start',
                      '& .MuiChip-label': {
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                        overflow: 'visible',
                        fontSize: '0.68rem',
                        px: '5px',
                        py: '4px',
                        display: 'block',
                        lineHeight: 1.35,
                      },
                    }}
                  />
                ) : (
                  '—'
                )}
              </TableCell>
              <TableCell sx={{ ...RESUMEN_COL.ramo, py: 1, verticalAlign: 'top', overflow: 'visible' }}>
                <ChipClasificacion nivel="ramo" nombre={row.nombreRamo ?? ''} />
              </TableCell>
              <TableCell sx={{ ...RESUMEN_COL.linea, py: 1, verticalAlign: 'top', overflow: 'visible' }}>
                <ChipClasificacion nivel="linea" nombre={row.nombreLinea ?? ''} />
              </TableCell>
              <TableCell sx={{ ...RESUMEN_COL.clase, py: 1, verticalAlign: 'top', overflow: 'visible' }}>
                <ChipClasificacion nivel="clase" nombre={row.nombreClase ?? ''} />
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  ...RESUMEN_COL.cantidad,
                  verticalAlign: 'top',
                  fontSize: '0.75rem',
                  whiteSpace: 'nowrap',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {qtyFormatter.format(row.cantidad)}
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  ...RESUMEN_COL.monto,
                  verticalAlign: 'top',
                  fontSize: '0.75rem',
                  whiteSpace: 'nowrap',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {currencyFormatter.format(row.montoTotal)}
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  ...RESUMEN_COL.pctMonto,
                  verticalAlign: 'top',
                  fontSize: '0.75rem',
                  whiteSpace: 'nowrap',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {pctFormatter.format(pctDelMonto(row.montoTotal, totalMonto))}%
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  ...RESUMEN_COL.costo,
                  verticalAlign: 'top',
                  fontSize: '0.75rem',
                  whiteSpace: 'nowrap',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {currencyFormatter.format(row.costoTotal ?? 0)}
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  ...RESUMEN_COL.pctRentab,
                  verticalAlign: 'top',
                  fontSize: '0.75rem',
                  whiteSpace: 'nowrap',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {fmtPctRentab(row.montoTotal, row.costoTotal ?? 0)}
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  ...RESUMEN_COL.peso,
                  verticalAlign: 'top',
                  fontSize: '0.75rem',
                  whiteSpace: 'nowrap',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {decimal3Formatter.format(row.pesoTotal)}
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  ...RESUMEN_COL.volumen,
                  verticalAlign: 'top',
                  fontSize: '0.75rem',
                  whiteSpace: 'nowrap',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {decimal3Formatter.format(row.volumenTotal)}
              </TableCell>
              <TableCell align="center" className="no-print" sx={{ ...RESUMEN_COL.accion, px: 0.25, verticalAlign: 'top' }}>
                <IconButton
                  size="small"
                  color="info"
                  aria-label="Ver detalle"
                  onClick={(e) => {
                    e.stopPropagation();
                    onAbrirDetalle(row);
                  }}
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
            );
          })}
          <TableRow
            sx={{
              bgcolor: '#e3f2fd',
              '& td': {
                fontWeight: 700,
                color: theme.palette.primary.main,
                borderTop: `2px solid ${theme.palette.primary.light}`,
              },
            }}
          >
            <TableCell colSpan={5} sx={{ fontSize: '0.8rem' }}>
              {filtroDrillDown ? 'TOTAL FILTRADO' : 'TOTAL GENERAL'}
            </TableCell>
            <TableCell
              align="right"
              sx={{ ...RESUMEN_COL.cantidad, fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums' }}
            >
              {qtyFormatter.format(totales.totalCantidad)}
            </TableCell>
            <TableCell
              align="right"
              sx={{ ...RESUMEN_COL.monto, fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums' }}
            >
              {currencyFormatter.format(totales.totalMonto)}
            </TableCell>
            <TableCell align="right" sx={{ ...RESUMEN_COL.pctMonto, fontSize: '0.75rem' }}>
              100%
            </TableCell>
            <TableCell
              align="right"
              sx={{ ...RESUMEN_COL.costo, fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums' }}
            >
              {currencyFormatter.format(totales.totalCosto)}
            </TableCell>
            <TableCell
              align="right"
              sx={{ ...RESUMEN_COL.pctRentab, fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums' }}
            >
              {fmtPctRentab(totales.totalMonto, totales.totalCosto)}
            </TableCell>
            <TableCell
              align="right"
              sx={{ ...RESUMEN_COL.peso, fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums' }}
            >
              {decimal3Formatter.format(totales.totalPeso)}
            </TableCell>
            <TableCell
              align="right"
              sx={{ ...RESUMEN_COL.volumen, fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums' }}
            >
              {decimal3Formatter.format(totales.totalVolumen)}
            </TableCell>
            <TableCell className="no-print" sx={{ ...RESUMEN_COL.accion }} />
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
      )}
    </Box>
  );
};

export const ResumenClasificacionTabs: React.FC<ResumenClasificacionTabsProps> = ({
  rows,
  rowsMensuales,
  totalMonto,
  totales,
  onAbrirDetalle,
}) => {
  const [tab, setTab] = React.useState(0);
  const [filtroDrillDown, setFiltroDrillDown] = React.useState<FiltroDrillDown | null>(null);
  const [productoSeleccionado, setProductoSeleccionado] = React.useState<VentasResumenProductoRow | null>(null);

  React.useEffect(() => {
    setFiltroDrillDown(null);
    setProductoSeleccionado(null);
  }, [rows]);

  const handleDrillDown = React.useCallback((nivel: NivelChip, nombre: string) => {
    setFiltroDrillDown({ nivel, nombre });
    setProductoSeleccionado(null);
    setTab(TAB_PRODUCTO);
  }, []);

  const rowsProducto = useMemo(() => {
    if (!filtroDrillDown) return rows;
    return filtrarRowsPorDrillDown(rows, filtroDrillDown);
  }, [rows, filtroDrillDown]);

  const rowsMensualesProducto = useMemo(() => {
    if (!filtroDrillDown) return rowsMensuales;
    return filtrarRowsPorDrillDown(rowsMensuales, filtroDrillDown);
  }, [rowsMensuales, filtroDrillDown]);

  React.useEffect(() => {
    if (!productoSeleccionado) return;
    const sigueVisible = rowsProducto.some((r) => isSameProductoResumenRow(r, productoSeleccionado));
    if (!sigueVisible) setProductoSeleccionado(null);
  }, [rowsProducto, productoSeleccionado]);

  const totalesProducto = useMemo(() => {
    if (!filtroDrillDown) return totales;
    const t = sumRows(rowsProducto);
    return {
      totalCantidad: t.cantidad,
      totalMonto: t.montoTotal,
      totalCosto: t.costoTotal,
      totalPeso: t.pesoTotal,
      totalVolumen: t.volumenTotal,
    };
  }, [rowsProducto, filtroDrillDown, totales]);

  const montoBaseProducto = filtroDrillDown ? totalesProducto.totalMonto : totalMonto;

  const porRamo = useMemo(
    () => aggregateByField(rows, 'nombreRamo', totalMonto),
    [rows, totalMonto]
  );
  const porLinea = useMemo(
    () => aggregateByField(rows, 'nombreLinea', totalMonto),
    [rows, totalMonto]
  );
  const porClase = useMemo(
    () => aggregateByField(rows, 'nombreClase', totalMonto),
    [rows, totalMonto]
  );

  return (
    <Box sx={{ mb: 2 }} className="informe-resumen-clasificacion" data-tour-id="informe-resultados">
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
        Resumen por clasificación
      </Typography>
      <Tabs
        value={tab}
        onChange={(_, v) => {
          setTab(v);
          if (v !== TAB_PRODUCTO) {
            setFiltroDrillDown(null);
            setProductoSeleccionado(null);
          }
        }}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 1, minHeight: 40, '& .MuiTab-root': { minHeight: 40, py: 0.5, textTransform: 'none' } }}
      >
        <Tab label="Ramo" />
        <Tab
          label={
            <Tooltip
              title="Total por nombre de línea en el período filtrado (puede repetirse en varios ramos)."
              arrow
            >
              <span>Línea</span>
            </Tooltip>
          }
        />
        <Tab label="Clase" />
        <Tab label="Producto" />
      </Tabs>

      {tab === TAB_RAMO && (
        <Box>
          <GraficoRamosMensual rowsMensuales={rowsMensuales} />
          <TablaAgregada label="Ramo" nivel="ramo" filas={porRamo} totales={totales} onDrillDown={handleDrillDown} />
        </Box>
      )}
      {tab === TAB_LINEA && (
        <Box>
          <GraficosMensualesPorCampo
            rowsMensuales={rowsMensuales}
            field="nombreLinea"
            titulo="Ventas por línea"
          />
          <TablaAgregada label="Línea" nivel="linea" filas={porLinea} totales={totales} onDrillDown={handleDrillDown} />
        </Box>
      )}
      {tab === TAB_CLASE && (
        <Box>
          <GraficosMensualesPorCampo
            rowsMensuales={rowsMensuales}
            field="nombreClase"
            titulo="Ventas por clase"
          />
          <TablaAgregada label="Clase" nivel="clase" filas={porClase} totales={totales} onDrillDown={handleDrillDown} />
        </Box>
      )}
      {tab === TAB_PRODUCTO && (
        <Box>
          <GraficoProductoMensual
            rowsMensuales={rowsMensualesProducto}
            producto={productoSeleccionado}
          />
          <TablaProductos
            rows={rowsProducto}
            totalMonto={montoBaseProducto}
            totales={totalesProducto}
            filtroDrillDown={filtroDrillDown}
            productoSeleccionado={productoSeleccionado}
            onLimpiarFiltro={() => setFiltroDrillDown(null)}
            onSeleccionarProducto={setProductoSeleccionado}
            onAbrirDetalle={onAbrirDetalle}
          />
        </Box>
      )}
    </Box>
  );
};
